import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowRight, BadgeCheck, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const TIME_SLOTS = [
  "Today · within 2 hours",
  "Today · evening",
  "Tomorrow · morning",
  "Tomorrow · afternoon",
  "This week",
  "Next week",
];

export default function SaasInquiryDialog({
  open,
  onOpenChange,
  cta = "book_demo",
  title = "Book a 30-min demo",
  subtitle = "We'll set up a custom-branded sandbox before the call.",
  onSubmitted,
  onPickSlot,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    whatsapp: "",
    email: "",
    company_name: "",
    location: "",
    best_time: "",
    message: "",
  });

  // reset state every time the dialog opens for a new CTA
  useEffect(() => {
    if (open) {
      setDone(false);
      setSubmitting(false);
    }
  }, [open, cta]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/saas/inquiries", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        whatsapp: (form.whatsapp || form.phone).trim(),
        email: form.email.trim(),
        company_name: form.company_name.trim(),
        location: form.location.trim() || null,
        best_time: form.best_time || null,
        cta,
        source: "saas_landing",
        message: form.message.trim() || null,
      });
      setDone(true);
      toast.success("Got it. Our team will reach out shortly.", {
        duration: 4000,
      });
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="saas-inquiry-dialog"
        className="sm:max-w-2xl bg-[#FAF9F6] border-[#0A2540]/15 p-0 overflow-y-auto max-h-[90dvh]"
      >
        {/* gold edge */}
        <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[#C9A961] to-transparent" />

        {done ? (
          <div className="px-8 py-12 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#C9A961]/15 flex items-center justify-center">
              <BadgeCheck className="h-7 w-7 text-[#0A2540]" />
            </div>
            <DialogTitle className="font-serif-display text-3xl text-[#0A2540] mt-6">
              Thank you, <span className="italic text-[#C9A961]">{form.name.split(" ")[0]}</span>.
            </DialogTitle>
            <p className="mt-4 text-[#0A2540]/70 max-w-sm mx-auto">
              Your details landed in our CRM. A senior team member will reach out
              {form.best_time ? ` ${form.best_time.toLowerCase()}` : " shortly"}.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="inquiry-done-close"
                onClick={() => onOpenChange(false)}
                className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
              >
                Close
              </Button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  `Hi MedAdmits — I'm ${form.name} from ${form.company_name}. Just submitted a demo request.`
                )}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center px-5 py-2 rounded-md border border-[#0A2540]/20 text-[#0A2540] text-sm font-medium hover:bg-white"
              >
                Or message us on WhatsApp
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </a>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader className="px-8 pt-8 pb-2">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">
                <Sparkles className="h-3.5 w-3.5" />
                MedAdmits · Sales
              </div>
              <DialogTitle className="font-serif-display text-3xl text-[#0A2540] mt-3 leading-tight">
                {title}
              </DialogTitle>
              <DialogDescription className="text-[#0A2540]/65 text-sm">
                {subtitle}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="px-8 pb-8 pt-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Your name" required>
                  <Input
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Amit Sharma"
                    data-testid="inquiry-name"
                    className="bg-white"
                  />
                </Field>
                <Field label="Company / Consultancy" required>
                  <Input
                    required
                    value={form.company_name}
                    onChange={set("company_name")}
                    placeholder="Sunrise MBBS Consultancy"
                    data-testid="inquiry-company"
                    className="bg-white"
                  />
                </Field>
                <Field label="Phone" required>
                  <Input
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91 98•••• ••••"
                    inputMode="tel"
                    data-testid="inquiry-phone"
                    className="bg-white"
                  />
                </Field>
                <Field
                  label="WhatsApp"
                  hint="Leave blank to use phone number"
                >
                  <Input
                    value={form.whatsapp}
                    onChange={set("whatsapp")}
                    placeholder="Same as phone"
                    inputMode="tel"
                    data-testid="inquiry-whatsapp"
                    className="bg-white"
                  />
                </Field>
                <Field label="Email" required>
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="amit@sunrisembbs.in"
                    data-testid="inquiry-email"
                    className="bg-white"
                  />
                </Field>
                <Field label="Location">
                  <Input
                    value={form.location}
                    onChange={set("location")}
                    placeholder="Pune, Maharashtra"
                    data-testid="inquiry-location"
                    className="bg-white"
                  />
                </Field>
              </div>

              <Field label="Best time to connect">
                <div className="flex flex-wrap gap-2">
                  {TIME_SLOTS.map((slot) => {
                    const active = form.best_time === slot;
                    return (
                      <button
                        type="button"
                        key={slot}
                        data-testid={`inquiry-time-${slot.replace(/\W+/g, "-").toLowerCase()}`}
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            best_time: active ? "" : slot,
                          }))
                        }
                        className={`px-3.5 py-1.5 rounded-full text-xs border transition-all ${
                          active
                            ? "bg-[#0A2540] border-[#0A2540] text-white"
                            : "bg-white border-[#0A2540]/15 text-[#0A2540]/70 hover:border-[#C9A961] hover:text-[#0A2540]"
                        }`}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="Anything we should know? (optional)">
                <Textarea
                  rows={2}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Current monthly leads, current tools, biggest blocker…"
                  data-testid="inquiry-message"
                  className="bg-white resize-none"
                />
              </Field>

              <div className="pt-2 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <div className="flex flex-col gap-1">
                  <p className="text-[11px] text-[#0A2540]/50">
                    By submitting you agree to a one-time outreach call. No spam.
                  </p>
                  {onPickSlot && (
                    <button
                      type="button"
                      onClick={onPickSlot}
                      data-testid="inquiry-pick-slot"
                      className="text-left text-[12px] text-[#0A2540]/70 hover:text-[#C9A961] inline-flex items-center gap-1.5 link-shine self-start"
                    >
                      Or pick a slot now
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="inquiry-submit"
                  className="btn-luxury text-white px-7 py-3 rounded-full text-sm font-medium tracking-wide disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send to sales team"}
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/65">
        {label}
        {required && <span className="text-[#C9A961] ml-1">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && (
        <div className="mt-1 text-[10px] text-[#0A2540]/45">{hint}</div>
      )}
    </div>
  );
}
